import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BUDGET_USER } from 'business/constants';
import { UserT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';

import { updateUserRights } from './updateUserRights';
import { UserCreationFormState } from '../CreateUserContext/types';
import { updateEmailToLowercase } from '../helpers/email';
import { BUDGET_USER_RIGHTS_RESOURCES } from '../../helpers';
import { UserCreationType } from '../../CreateMultipleUser/context/types';
import { userPreProcessingBodyCallback } from '../helpers/preProcessingUserbody';

export const useCreateUsers = (
    usersData: UserCreationType[],
    shouldSendActivationEmail: boolean,
    onSuccess: (users: UserT[], message: string) => void = () => {},
    onError: (err: HttpError) => void = () => {},
) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const response = await RQAPICallWithData<UserT[]>({
                body: {
                    newUsersData: usersData.map((u) =>
                        updateEmailToLowercase(u),
                    ),
                    shouldSendActivationEmail,
                },
                endpoint: 'auth/users',
                method: 'POST',
                preProcessingBodyCallback: userPreProcessingBodyCallback,
            });
            return response.content;
        },

        onSuccess: ({ data: createdUsers, message }) => {
            dispatch.vision.notifyUser({
                message,
                snackType: 'info',
            });
            queryClient.setQueryData(['users'], (oldUsers: UserT[]) => [
                ...oldUsers,
                ...createdUsers,
            ]);
            createdUsers.forEach((u) =>
                trackEvent('new-user-created', {
                    email: u.email,
                }),
            );
            onSuccess(createdUsers, message);
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
            onError(error);
        },
    });
};

/**
 * RQ Hook to create a new user
 * @param {UserCreationFormState} userData - The data used to create the new user
 * @param {boolean} shouldSendActivationEmail - True if the created user should receive a mail indicating his/her account was created.
 * @param {boolean} hasBudgetModule - True if the budget module is available for the client.
 */
export const useCreateUser = (
    userData: UserCreationFormState,
    hasBudgetModule: boolean,
    shouldSendActivationEmail: boolean,
) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const response = await RQAPICallWithData<UserT>({
                body: {
                    newUserData: {
                        // We lower the email provided before storing it into the DB
                        ...updateEmailToLowercase(userData),
                    },
                    shouldSendActivationEmail,
                },
                endpoint: 'auth/user',
                method: 'POST',
            });
            return response.content;
        },

        onSuccess: ({ data: createdUser, message }) => {
            trackEvent('new-user-created', {
                email: createdUser.email,
            });
            dispatch.vision.notifyUser({
                message,
                snackType: 'info',
            });
            queryClient.setQueryData(['users'], (oldUsers: UserT[]) => [
                ...oldUsers,
                createdUser,
            ]);
            if (
                hasBudgetModule &&
                userData.userRights &&
                userData.roles.includes(BUDGET_USER)
            ) {
                updateUserRights(
                    createdUser.id,
                    userData.userRights,
                    BUDGET_USER_RIGHTS_RESOURCES,
                    queryClient,
                );
            }
        },
        meta: { useGlobalOnError: true },
    });
};
